import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import swal from "sweetalert";
import PaginationComponent from "../Datatable/Pagination/Index";
import UseFullPageLoader from "../FullPageLoader/UseFullPageLoader";

const NearbyContractorListByUser = () => {
  document.title = "DirtMap - Nearby Truck Companies";

  const [loading, setLoading] = useState(true);
  const [postList, setPostList] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Defalut 15 
  const [miles, setMiles] = useState(15);

  // save Current latitude and longitude  .
  const [currentLocation, setCurrentLocation] = useState({
    lat: null,
    lng: null,
  });

  const ITEMS_PER_PAGE = 10;
  const [loader, showLoader, hideLoader] = UseFullPageLoader();

  const getCurrentLocationAndFetch = () => {
    if (!navigator.geolocation) {
      setLoading(false);
      swal("Error", "Geolocation not supported", "error");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        // Current location save in state
        setCurrentLocation({
          lat: lat,
          lng: lng,
        });

        // API call with latitude, longitude and miles.
        getNearbyPosts(lat, lng, miles);
      },
      (error) => {
        console.error("Location error:", error);
        setLoading(false);

        swal(
          "Error",
          "Unable to fetch location. Please allow location permission.",
          "error"
        );
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 60000,
      }
    );
  };

  const getNearbyPosts = async (lat, lng, m = miles) => {
    try {
      showLoader();

      const userId = localStorage.getItem("user_id");

      if (!userId) {
        swal("Error", "User not logged in", "error");
        setPostList([]);
        setTotalItems(0);
        setLoading(false);
        return;
      }

      if (lat === null || lng === null) {
        swal("Error", "Location is not available", "error");
        setLoading(false);
        return;
      }

      const res = await axios.post(
        "/api/nearby/contractors/by-user",
        {
          user_id: Number(userId),
          lat: Number(lat),
          lng: Number(lng),
          miles: Number(m),
          flag: "",
        },
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      // Handle result.data OR result.
      const result =
        res.data?.result?.data ??
        res.data?.result ??
        [];

      const finalResult = Array.isArray(result) ? result : [];

      setPostList(finalResult);
      setTotalItems(finalResult.length);
      setCurrentPage(1);
      setLoading(false);
    } catch (err) {
      console.error("Nearby contractor error:", err);

      setPostList([]);
      setTotalItems(0);
      setLoading(false);

      swal(
        "Error",
        err?.response?.data?.message ||
          "Failed to load Nearby Truck Companies",
        "error"
      );
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    getCurrentLocationAndFetch();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMilesChange = (e) => {
    const selectedMiles = Number(e.target.value);

    setMiles(selectedMiles);

    // Saved latitude and longitude sent new radius.
    if (
      currentLocation.lat !== null &&
      currentLocation.lng !== null
    ) {
      getNearbyPosts(
        currentLocation.lat,
        currentLocation.lng,
        selectedMiles
      );
    } else {
      // if Location save then location fetch
      getCurrentLocationAndFetch();
    }
  };

  const paginatedData = useMemo(() => {
    const computed = postList || [];

    return computed.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE +
        ITEMS_PER_PAGE
    );
  }, [postList, currentPage]);

  if (loading) {
    return (
      <div className="fp-container">
        <img
          src="/images/spinner.gif"
          className="fp-loader"
          alt="loading"
        />
      </div>
    );
  }

  return (
    <>
      <section className="breadcrumb">
        <div className="container">
          <div className="row">
            <div className="col-md-12 bread-crumb">
              <Link to="/">Home</Link>

              <span>&gt;</span>

              Nearby Truck Companies
            </div>
          </div>
        </div>

        <hr />
      </section>

      <section className="contentContainer">
        <div className="container">
          {/* Radius filter */}
          <div
            className="row"
            style={{ marginBottom: 15 }}
          >
            <div className="col-md-4">
              <label style={{ fontWeight: 600 }}>
                Radius (miles)
              </label>

              <select
                className="form-control"
                value={miles}
                onChange={handleMilesChange}
              >
                <option value="15">
                  15 Miles
                </option>

                <option value="30">
                  30 Miles
                </option>

                <option value="50">
                  50 Miles
                </option>

                <option value="100">
                  100 Miles
                </option>
              </select>
            </div>
          </div>

          {paginatedData.length === 0 ? (
            <div className="favoritesList shadowBox">
              <div className="row">
                <div className="col-md-12">
                  <p style={{ margin: 0 }}>
                    No nearby posts found within {miles} miles.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            paginatedData.map((item) => (
              <div
                className="favoritesList shadowBox"
                key={item.id || item.post_id}
              >
                <div className="row">
                  <div className="col-md-12 actionButtons">
                    <h2 className="bottomBorder">
                      {item.project_name}
                    </h2>

                    <p className="truckInfo bottomBorder">
                      <span>
                        <i
                          className="fa fa-truck"
                          aria-hidden="true"
                        />

                        {item.company_name}
                      </span>

                      <span className="space">
                        &nbsp;
                      </span>

                      <span>
                        <i
                          className="fa fa-map-marker"
                          aria-hidden="true"
                        />

                        {item.post_address}
                      </span>
                    </p>

                    <p>{item.description}</p>

                    <Link
                      className="button customButton01"
                      to={`/detail/${item.post_id}`}
                    >
                      View Full Detail
                    </Link>
                  </div>
                </div>
              </div>
            ))
          )}

          {totalItems > ITEMS_PER_PAGE && (
            <div className="text-right">
              <PaginationComponent
                total={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                onPageChange={(page) =>
                  setCurrentPage(page)
                }
              />
            </div>
          )}
        </div>
      </section>

      {loader}
    </>
  );
};

export default NearbyContractorListByUser;